import React, { useEffect, useMemo, useState } from 'react';
import { content } from './content';
import { productCatalog } from './productCatalog';
import { useScrollReveal, useCountUp, useLanguage } from './hooks';

function LoopMark({ className = '' }) {
  return (
    <svg className={`loop-mark ${className}`} viewBox="0 0 48 48" aria-hidden="true">
      <defs>
        <linearGradient id="loopGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--accent-strong)" />
          <stop offset="100%" stopColor="var(--accent)" />
        </linearGradient>
      </defs>
      <g fill="none" stroke="url(#loopGrad)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 30a12 12 0 0 1 11-18 12 12 0 0 1 11 7" />
        <path d="M34 18a12 12 0 0 1-11 18 12 12 0 0 1-11-7" />
      </g>
      <g fill="url(#loopGrad)">
        <path d="M33 6l5 7h-10z" />
        <path d="M15 42l-5-7h10z" />
      </g>
    </svg>
  );
}

function Brand({ href = '/', onClick }) {
  return (
    <a className="brand" href={href} aria-label="dooloop" onClick={onClick}>
      <LoopMark />
      <span className="brand-name">
        d<span className="brand-o">oo</span>l<span className="brand-o">oo</span>p
      </span>
    </a>
  );
}

function getRoute() {
  if (typeof window === 'undefined') return { page: 'home' };
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (path === '/') return { page: 'home' };
  const match = path.match(/^\/products\/([^/]+)$/);
  if (match) return { page: 'product', slug: match[1] };
  return { page: 'home' };
}

function useRoute() {
  const [route, setRoute] = useState(getRoute);

  useEffect(() => {
    const onPopState = () => setRoute(getRoute());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = (href) => {
    if (typeof window === 'undefined') return;
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) {
      window.location.href = href;
      return;
    }
    window.history.pushState({}, '', `${url.pathname}${url.search}${url.hash}`);
    setRoute(getRoute());
    if (url.hash) {
      requestAnimationFrame(() => {
        const node = document.getElementById(url.hash.slice(1));
        if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return [route, navigate];
}

function AppLink({ href, navigate, className, children, onClick }) {
  return (
    <a
      href={href}
      className={className}
      onClick={(e) => {
        if (onClick) onClick(e);
        if (
          e.defaultPrevented ||
          e.metaKey ||
          e.ctrlKey ||
          e.shiftKey ||
          e.altKey ||
          e.button !== 0
        ) {
          return;
        }
        e.preventDefault();
        navigate(href);
      }}
    >
      {children}
    </a>
  );
}

function Nav({ t, lang, onToggleLang, route, navigate }) {
  const [open, setOpen] = useState(false);
  const homeLinks = [
    { label: t.nav.products, href: '/#products' },
    { label: t.nav.method, href: '/#method' },
    { label: t.nav.why, href: '/#why' }
  ];
  const productLinks = [
    { label: t.nav.products, href: '/#products' },
    { label: t.productPage.navCapabilities, href: '#capabilities' },
    { label: t.productPage.navStack, href: '#stack' },
    { label: t.productPage.navRoadmap, href: '#roadmap' }
  ];
  const links = route.page === 'product' ? productLinks : homeLinks;

  return (
    <header className="topbar">
      <div className="topbar-inner">
        <Brand href="/" onClick={(e) => { e.preventDefault(); navigate('/'); setOpen(false); }} />

        <nav className={`topnav ${open ? 'is-open' : ''}`} aria-label="primary">
          {links.map((item) =>
            item.href.startsWith('#') ? (
              <a key={item.href} href={item.href} onClick={() => setOpen(false)}>
                {item.label}
              </a>
            ) : (
              <AppLink key={item.href} href={item.href} navigate={navigate} onClick={() => setOpen(false)}>
                {item.label}
              </AppLink>
            )
          )}
          <a className="topnav-cta-mobile button button-primary" href="/#waitlist" onClick={() => setOpen(false)}>
            {t.nav.cta}
          </a>
        </nav>

        <div className="topbar-actions">
          <button className="lang-toggle" onClick={onToggleLang} aria-label="Switch language">
            <span className={lang === 'pt' ? 'is-active' : ''}>PT</span>
            <span aria-hidden="true">/</span>
            <span className={lang === 'en' ? 'is-active' : ''}>EN</span>
          </button>
          <a className="button button-primary topbar-cta" href="/#waitlist">
            {t.nav.cta}
          </a>
          <button
            className={`menu-toggle ${open ? 'is-open' : ''}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            aria-expanded={open}
          >
            <span />
            <span />
          </button>
        </div>
      </div>
    </header>
  );
}

function CheckIcon() {
  return (
    <svg className="check-icon" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg className="arrow-icon" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M3 8h9" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M8.5 3.5L13 8l-4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SectionHead({ eyebrow, title, subtitle, center }) {
  return (
    <div className={`section-head ${center ? 'is-center' : ''}`} data-reveal>
      <span className="eyebrow">
        <span className="eyebrow-dot" />
        {eyebrow}
      </span>
      <h2>{title}</h2>
      {subtitle && <p>{subtitle}</p>}
    </div>
  );
}

function Hero({ hero }) {
  return (
    <section className="hero" id="top">
      <div className="hero-copy">
        <span className="eyebrow" data-reveal>
          <span className="eyebrow-dot" />
          {hero.badge}
        </span>

        <h1 data-reveal style={{ '--reveal-delay': '60ms' }}>
          {hero.titleA} <span className="text-gradient">{hero.titleHighlight}</span>
        </h1>

        <p className="hero-text" data-reveal style={{ '--reveal-delay': '120ms' }}>
          {hero.subtitle}
        </p>

        <div className="hero-actions" data-reveal style={{ '--reveal-delay': '180ms' }}>
          <a className="button button-primary button-lg" href="#waitlist">
            {hero.ctaPrimary}
          </a>
          <a className="button button-secondary button-lg" href="#products">
            {hero.ctaSecondary}
          </a>
        </div>

        <ul className="hero-proof" data-reveal style={{ '--reveal-delay': '240ms' }}>
          {hero.proof.map((item) => (
            <li key={item}>
              <CheckIcon />
              {item}
            </li>
          ))}
        </ul>
      </div>

      <div className="hero-visual" aria-hidden="true" data-reveal style={{ '--reveal-delay': '160ms' }}>
        <div className="terminal-card">
          <div className="terminal-top">
            <span />
            <span />
            <span />
            <em>{hero.terminal.title}</em>
          </div>
          <div className="terminal-body">
            {hero.terminal.lines.map((line) => (
              <div className="terminal-line" key={line.key}>
                <span className="terminal-prompt">›</span>
                <span className="terminal-key">{line.key}</span>
                <span className="terminal-value">{line.value}</span>
              </div>
            ))}
            <div className="terminal-line terminal-running">
              <span className="terminal-prompt">›</span>
              <span className="terminal-cursor" />
            </div>
          </div>
        </div>
        <div className="hero-glow-card hero-glow-coral" />
        <div className="hero-glow-card hero-glow-blue" />
      </div>
    </section>
  );
}

function Marquee({ items }) {
  const row = [...items, ...items];
  return (
    <div className="marquee" aria-hidden="true">
      <div className="marquee-track">
        {row.map((item, i) => (
          <span key={`${item}-${i}`}>
            {item}
            <span className="marquee-sep" />
          </span>
        ))}
      </div>
    </div>
  );
}

function Products({ section, navigate }) {
  return (
    <section className="section" id="products">
      <SectionHead eyebrow={section.eyebrow} title={section.title} subtitle={section.subtitle} />
      <div className="products-grid products-grid-expanded">
        {section.items.map((item, i) => (
          <article className="product-card product-card-link" key={item.slug} data-reveal style={{ '--reveal-delay': `${i * 80}ms` }}>
            <span className="product-tag">{item.tag}</span>
            <h3>{item.title}</h3>
            <p>{item.text}</p>
            <ul className="product-points">
              {item.points.map((p) => (
                <li key={p}>
                  <CheckIcon />
                  {p}
                </li>
              ))}
            </ul>
            <AppLink className="product-link" href={`/products/${item.slug}`} navigate={navigate}>
              {section.more}
              <ArrowIcon />
            </AppLink>
          </article>
        ))}
      </div>
    </section>
  );
}

function Method({ method }) {
  return (
    <section className="section method band-dark" id="method">
      <SectionHead eyebrow={method.eyebrow} title={method.title} subtitle={method.subtitle} center />
      <div className="method-shell">
        <div className="loop-diagram" data-reveal aria-hidden="true">
          <svg viewBox="0 0 200 200">
            <circle className="loop-ring-bg" cx="100" cy="100" r="78" />
            <circle className="loop-ring" cx="100" cy="100" r="78" />
          </svg>
          <div className="loop-core">
            <LoopMark className="loop-core-mark" />
            <span>{method.loopLabel}</span>
          </div>
        </div>

        <ol className="method-steps">
          {method.steps.map((step, i) => (
            <li className="method-step" key={step.n} data-reveal style={{ '--reveal-delay': `${i * 90}ms` }}>
              <span className="method-step-n">{step.n}</span>
              <div>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Why({ why }) {
  return (
    <section className="section" id="why">
      <SectionHead eyebrow={why.eyebrow} title={why.title} />
      <div className="why-grid">
        {why.cards.map((card, i) => (
          <article className="why-card" key={card.title} data-reveal style={{ '--reveal-delay': `${i * 70}ms` }}>
            <span className="why-dot" />
            <h3>{card.title}</h3>
            <p>{card.text}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function Stat({ value, suffix, label }) {
  const [ref, current] = useCountUp(value);
  return (
    <article className="stat" ref={ref}>
      <strong>
        {current}
        {suffix}
      </strong>
      <span>{label}</span>
    </article>
  );
}

function Stats({ items }) {
  return (
    <section className="section stats-section">
      <div className="stats" data-reveal>
        {items.map((s) => (
          <Stat key={s.label} {...s} />
        ))}
      </div>
    </section>
  );
}

function Waitlist({ waitlist }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!valid) {
      setStatus('invalid');
      return;
    }
    setStatus('sending');

    const endpoint = process.env.REACT_APP_WAITLIST_ENDPOINT || '/api/waitlist';
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'waitlist' })
      });
      if (!res.ok) throw new Error('request failed');
      setStatus('success');
      setEmail('');
    } catch (err) {
      setStatus('error');
    }
  };

  const message =
    status === 'success'
      ? { text: waitlist.success, tone: 'ok' }
      : status === 'error'
      ? { text: waitlist.error, tone: 'err' }
      : status === 'invalid'
      ? { text: waitlist.invalid, tone: 'err' }
      : null;

  return (
    <section className="section waitlist-section band-dark" id="waitlist">
      <div className="waitlist-panel" data-reveal>
        <div className="waitlist-glow" aria-hidden="true" />
        <span className="eyebrow">
          <span className="eyebrow-dot" />
          {waitlist.eyebrow}
        </span>
        <h2>{waitlist.title}</h2>
        <p>{waitlist.subtitle}</p>
        <form className="waitlist-form" onSubmit={handleSubmit} noValidate>
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (status !== 'idle' && status !== 'sending') setStatus('idle');
            }}
            placeholder={waitlist.placeholder}
            aria-label={waitlist.placeholder}
            autoComplete="email"
          />
          <button className="button button-primary button-lg" type="submit" disabled={status === 'sending'}>
            {status === 'sending' ? waitlist.sending : waitlist.button}
          </button>
        </form>
        <div className={`waitlist-msg ${message ? `is-${message.tone}` : ''}`} role="status" aria-live="polite">
          {message ? message.text : waitlist.disclaimer}
        </div>
      </div>
    </section>
  );
}

function Footer({ t, navigate }) {
  const links = [
    { label: t.footer.home, href: '/' },
    { label: t.nav.products, href: '/#products' },
    { label: t.nav.method, href: '/#method' },
    { label: t.nav.why, href: '/#why' }
  ];

  return (
    <footer className="footer">
      <div className="footer-grid">
        <div className="footer-brand">
          <Brand href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} />
          <p>{t.footer.tagline}</p>
        </div>

        <nav className="footer-nav" aria-label="footer">
          <span className="footer-col-title">{t.footer.nav}</span>
          {links.map((l) => (
            <AppLink key={l.href} href={l.href} navigate={navigate}>
              {l.label}
            </AppLink>
          ))}
        </nav>

        <div className="footer-contact">
          <span className="footer-col-title">{t.footer.emailLabel}</span>
          <a className="footer-email" href={`mailto:${t.footer.email}`}>
            {t.footer.email}
          </a>
          <a className="footer-top" href="#top">
            {t.footer.backToTop} ↑
          </a>
        </div>
      </div>
      <div className="footer-base">{t.footer.rights}</div>
    </footer>
  );
}

function HomePage({ t, navigate }) {
  return (
    <main>
      <Hero hero={t.home.hero} />
      <Marquee items={t.home.marquee} />
      <Products section={t.home.products} navigate={navigate} />
      <Method method={t.home.method} />
      <Why why={t.home.why} />
      <Stats items={t.home.stats} />
      <Waitlist waitlist={t.waitlist} />
    </main>
  );
}

function ProductHero({ product, navigate, ui, waitlistCta }) {
  return (
    <section className="product-hero" id="top">
      <div className="product-hero-copy">
        <AppLink className="product-breadcrumb" href="/#products" navigate={navigate}>
          <ArrowIcon />
          {product.kicker}
        </AppLink>
        <span className="eyebrow" data-reveal>
          <span className="eyebrow-dot" />
          {product.name}
        </span>
        <h1 data-reveal style={{ '--reveal-delay': '60ms' }}>{product.headline}</h1>
        <p className="product-summary" data-reveal style={{ '--reveal-delay': '120ms' }}>
          {product.summary}
        </p>
        <div className="hero-actions" data-reveal style={{ '--reveal-delay': '180ms' }}>
          <a className="button button-primary button-lg" href="#roadmap">{ui.ctaRoadmap}</a>
          <a className="button button-secondary button-lg" href="/#waitlist">{waitlistCta}</a>
        </div>
      </div>
      <div className="product-side-card" data-reveal style={{ '--reveal-delay': '160ms' }}>
        <span className="product-side-label">{ui.outcomes}</span>
        <ul className="product-points">
          {product.outcomes.map((item) => (
            <li key={item}>
              <CheckIcon />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function ProductPage({ product, navigate, waitlist, ui, waitlistCta }) {
  return (
    <main className="product-page">
      <ProductHero product={product} navigate={navigate} ui={ui} waitlistCta={waitlistCta} />

      <section className="section product-section" id="overview">
        <div className="product-grid-two">
          <article className="surface-card" data-reveal>
            <span className="section-kicker">{ui.description}</span>
            <h2>{ui.descriptionTitle}</h2>
            <p className="surface-text">{product.description}</p>
          </article>
          <article className="surface-card" data-reveal style={{ '--reveal-delay': '70ms' }}>
            <span className="section-kicker">{ui.servesFor}</span>
            <h2>{ui.servesForTitle}</h2>
            <p className="surface-text">{product.servesFor}</p>
          </article>
        </div>
      </section>

      <section className="section product-section">
        <div className="product-grid-two">
          <article className="surface-card" data-reveal>
            <span className="section-kicker">{ui.insideCompany}</span>
            <h2>{ui.insideCompanyTitle}</h2>
            <p className="surface-text">{product.insideCompany}</p>
          </article>
          <article className="surface-card" data-reveal style={{ '--reveal-delay': '70ms' }}>
            <span className="section-kicker">{ui.curiosityEyebrow}</span>
            <h2>{ui.curiosityTitle}</h2>
            <p className="surface-text surface-text-strong">{product.curiosity}</p>
          </article>
        </div>
      </section>

      <section className="section product-section">
        <div className="product-grid-two">
          <article className="surface-card" data-reveal>
            <span className="section-kicker">{ui.buyers}</span>
            <h2>{ui.buyersTitle}</h2>
            <div className="chip-list">
              {product.audience.map((item) => (
                <span className="chip" key={item}>{item}</span>
              ))}
            </div>
          </article>
          <article className="surface-card" data-reveal style={{ '--reveal-delay': '70ms' }}>
            <span className="section-kicker">{ui.pain}</span>
            <h2>{ui.painTitle}</h2>
            <ul className="surface-list">
              {product.pains.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>
        </div>
      </section>

      <section className="section product-section">
        <div className="product-grid-two">
          <article className="surface-card" data-reveal>
            <span className="section-kicker">{ui.companyUsageEyebrow}</span>
            <h2>{ui.companyUsageTitle}</h2>
            <ul className="surface-list">
              {product.companyUsage.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>
          <article className="surface-card" data-reveal style={{ '--reveal-delay': '70ms' }}>
            <span className="section-kicker">{ui.scenariosEyebrow}</span>
            <h2>{ui.scenariosTitle}</h2>
            <ul className="surface-list">
              {product.scenarios.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>
        </div>
      </section>

      <section className="section product-section" id="capabilities">
        <SectionHead eyebrow={ui.capabilitiesEyebrow} title={ui.capabilitiesTitle} subtitle={ui.capabilitiesSubtitle} />
        <div className="capability-grid">
          {product.capabilities.map((cap, i) => (
            <article className="surface-card" key={cap.title} data-reveal style={{ '--reveal-delay': `${i * 80}ms` }}>
              <h3>{cap.title}</h3>
              <ul className="surface-list">
                {cap.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="section product-section band-dark" id="process">
        <SectionHead eyebrow={ui.processEyebrow} title={ui.processTitle} subtitle={ui.processSubtitle} center />
        <ol className="product-flow">
          {product.workflow.map((step, i) => (
            <li className="product-flow-step" key={step} data-reveal style={{ '--reveal-delay': `${i * 90}ms` }}>
              <span>{String(i + 1).padStart(2, '0')}</span>
              <p>{step}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="section product-section" id="stack">
        <div className="product-grid-two">
          <article className="surface-card" data-reveal>
            <span className="section-kicker">{ui.integrations}</span>
            <h2>{ui.integrationsTitle}</h2>
            <ul className="surface-list">
              {product.integrations.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>
          <article className="surface-card" data-reveal style={{ '--reveal-delay': '80ms' }}>
            <span className="section-kicker">{ui.technology}</span>
            <h2>{ui.technologyTitle}</h2>
            <ul className="surface-list">
              {product.technology.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>
        </div>
      </section>

      <section className="section product-section" id="roadmap">
        <SectionHead eyebrow={ui.roadmapEyebrow} title={ui.roadmapTitle} subtitle={ui.roadmapSubtitle} />
        <div className="roadmap-grid">
          {product.roadmap.map((phase, i) => (
            <article className="roadmap-card" key={phase.phase} data-reveal style={{ '--reveal-delay': `${i * 90}ms` }}>
              <span className="product-tag">{phase.phase}</span>
              <h3>{phase.phase}</h3>
              <ul className="surface-list">
                {phase.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="section product-section" id="backlog">
        <SectionHead eyebrow={ui.backlogEyebrow} title={ui.backlogTitle} subtitle={ui.backlogSubtitle} />
        <div className="chip-list chip-list-large" data-reveal>
          {product.backlogThemes.map((item) => (
            <span className="chip chip-strong" key={item}>{item}</span>
          ))}
        </div>
      </section>

      <Waitlist waitlist={waitlist} />
    </main>
  );
}

function App() {
  const [lang, toggleLang] = useLanguage();
  const [route, navigate] = useRoute();
  const t = content[lang];
  const catalog = productCatalog[lang];
  const product = useMemo(
    () => (route.page === 'product' ? catalog.find((item) => item.slug === route.slug) : null),
    [catalog, route]
  );

  useScrollReveal([lang, route.page, route.slug]);

  useEffect(() => {
    const meta = route.page === 'product' && product
      ? {
          title: `${product.name} · dooloop`,
          description: product.summary
        }
      : t.meta;

    document.title = meta.title;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', meta.description);
  }, [route.page, product, t.meta]);

  return (
    <div className="page-shell">
      <Nav t={t} lang={lang} onToggleLang={toggleLang} route={route} navigate={navigate} />
      {route.page === 'product' && product ? (
        <ProductPage product={product} navigate={navigate} waitlist={t.waitlist} ui={t.productPage} waitlistCta={t.nav.cta} />
      ) : (
        <HomePage t={t} navigate={navigate} />
      )}
      <Footer t={t} navigate={navigate} />
    </div>
  );
}

export default App;
